import {
  type PropsWithChildren,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react"

export interface Toast {
  id: string
  title: string
  body: string
  variant: "info" | "success" | "warning" | "danger"
  action?: { label: string; path: string }
  createdAt: number
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, "id" | "createdAt">) => void
  dismissToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const AUTO_DISMISS_MS = 8_000
const MAX_VISIBLE = 5

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const addToast = useCallback(
    (input: Omit<Toast, "id" | "createdAt">) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const toast: Toast = { ...input, id, createdAt: Date.now() }

      setToasts((prev) => {
        const next = [toast, ...prev]
        return next.slice(0, MAX_VISIBLE)
      })

      const timer = setTimeout(() => {
        timersRef.current.delete(id)
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, AUTO_DISMISS_MS)

      timersRef.current.set(id, timer)
    },
    [],
  )

  const value: ToastContextValue = { toasts, addToast, dismissToast }

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within ToastProvider")
  }
  return context
}
